import React from 'react';
import './styles.scss';

const AboutPage: React.FC = () => {
  return (
    <div className="page-container">
      <div className="about-hero">
        <h1>About LLEO</h1>
        <p className="about-subtitle">Earth observation analysis, written in plain language.</p>
        <p className="about-link">
          <a href="https://sureal.ai/" target="_blank" rel="noopener noreferrer">
            A sureal.ai project →
          </a>
        </p>
      </div>

      <div className="about-content">
        <section className="about-section">
          <h2>What LLEO is</h2>
          <p>
            LLEO turns a question such as <em>“How has vegetation in Zurich changed since 2019?”</em> into
            a reproducible satellite analysis: it finds the place, picks the right dataset and index,
            runs the processing on Google Earth Engine, and returns an interactive map, charts and the
            underlying numbers. Every result can be revisited, shared with the community, and extended
            with follow-up questions in the same conversation.
          </p>
          <p>
            The project started from a simple observation: most of the data needed to understand a
            landscape is already public, but using it still requires a GIS background and a fair amount
            of plumbing. LLEO removes that plumbing.
          </p>
        </section>

        <section className="about-section">
          <h2>How a request is handled</h2>
          <ol className="about-steps">
            <li><strong>Understand</strong> — the question is parsed into a location, a time window, an analysis type and its parameters. If something essential is missing, LLEO asks instead of guessing.</li>
            <li><strong>Plan</strong> — the request is matched to a documented workflow (vegetation, water, air quality, timelapse, weather, places). Each workflow states which satellite product and index it uses.</li>
            <li><strong>Process</strong> — imagery is filtered, cloud‑masked and reduced on Google Earth Engine; non‑satellite sources are queried from their APIs.</li>
            <li><strong>Check</strong> — outputs are validated (coverage, image count, value ranges). If a step fails, the plan is adjusted and re‑run rather than returning an empty result.</li>
            <li><strong>Deliver</strong> — map, charts, animation (when applicable) and a details sheet with all inputs and statistics are stored so the analysis is reproducible.</li>
          </ol>
        </section>

        <section className="about-section">
          <h2>What you can analyse</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🌿</div>
              <h3>Vegetation</h3>
              <p>NDVI, EVI and SAVI from Sentinel‑2 to follow plant health, crop cycles and land‑cover change.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌫</div>
              <h3>Air quality</h3>
              <p>NO₂, CO, SO₂, O₃, CH₄ and aerosol index from Sentinel‑5P TROPOMI.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💧</div>
              <h3>Water quality</h3>
              <p>Turbidity, chlorophyll‑a, surface temperature and NDWI for lakes, rivers and coasts.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎬</div>
              <h3>Time‑lapse</h3>
              <p>Monthly or yearly animations of true‑colour or NDVI imagery to make change visible.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌤</div>
              <h3>Weather</h3>
              <p>Current conditions and a five‑day forecast for any location.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📍</div>
              <h3>Places</h3>
              <p>Points of interest, ratings and density maps for business and planning questions.</p>
            </div>
          </div>
        </section>

        <section className="about-section domains-section">
          <h2>Data sources</h2>
          <p>
            Each workflow is tied to a specific product so results are traceable. The cards below list
            the source, the period it covers and the variables LLEO derives from it.
          </p>

          <div className="domains-grid">
            {/* Vegetation */}
            <div className="domain-card">
              <div className="domain-header vegetation">
                <span className="domain-icon">🌿</span>
                <h3>Vegetation</h3>
              </div>
              <div className="domain-body">
                <div className="domain-meta">
                  <span className="domain-source">Sentinel-2</span>
                  <span className="domain-availability">June 2015 – present</span>
                </div>
                <p className="domain-desc">Multispectral optical imagery at 10 m resolution for plant health, land cover and agricultural productivity.</p>
                <div className="domain-indexes">
                  <span className="index-tag ndvi">NDVI</span>
                  <span className="index-tag evi">EVI</span>
                  <span className="index-tag savi">SAVI</span>
                </div>
                <ul className="index-details">
                  <li><strong>NDVI</strong> — Normalized Difference Vegetation Index</li>
                  <li><strong>EVI</strong> — Enhanced Vegetation Index (less atmospheric noise)</li>
                  <li><strong>SAVI</strong> — Soil-Adjusted Vegetation Index (arid / sparse cover)</li>
                </ul>
              </div>
            </div>

            {/* Air Quality */}
            <div className="domain-card">
              <div className="domain-header air-quality">
                <span className="domain-icon">🌫️</span>
                <h3>Air quality</h3>
              </div>
              <div className="domain-body">
                <div className="domain-meta">
                  <span className="domain-source">Sentinel-5P TROPOMI</span>
                  <span className="domain-availability">April 2018 – present</span>
                </div>
                <p className="domain-desc">Tropospheric columns of pollutants and greenhouse gases at roughly 5.5 km resolution.</p>
                <div className="domain-indexes">
                  <span className="index-tag no2">NO₂</span>
                  <span className="index-tag co">CO</span>
                  <span className="index-tag so2">SO₂</span>
                  <span className="index-tag o3">O₃</span>
                  <span className="index-tag ch4">CH₄</span>
                  <span className="index-tag aerosol">Aerosol</span>
                </div>
                <ul className="index-details">
                  <li><strong>NO₂</strong> — nitrogen dioxide (traffic and industry)</li>
                  <li><strong>CO</strong> — carbon monoxide (combustion)</li>
                  <li><strong>SO₂</strong> — sulfur dioxide (volcanic / industrial)</li>
                  <li><strong>O₃</strong> — tropospheric ozone</li>
                  <li><strong>CH₄</strong> — methane</li>
                  <li><strong>Aerosol</strong> — UV aerosol index (dust, smoke, ash)</li>
                </ul>
              </div>
            </div>

            {/* Water Quality */}
            <div className="domain-card">
              <div className="domain-header water-quality">
                <span className="domain-icon">💧</span>
                <h3>Water quality</h3>
              </div>
              <div className="domain-body">
                <div className="domain-meta">
                  <span className="domain-source">Sentinel-2</span>
                  <span className="domain-availability">June 2015 – present</span>
                </div>
                <p className="domain-desc">Optical analysis of inland and coastal water bodies for ecological health and resource management.</p>
                <div className="domain-indexes">
                  <span className="index-tag turbidity">Turbidity</span>
                  <span className="index-tag chlorophyll">Chlorophyll-a</span>
                  <span className="index-tag water-temp">Temperature</span>
                  <span className="index-tag ndwi">NDWI</span>
                </div>
                <ul className="index-details">
                  <li><strong>Turbidity</strong> — suspended sediment / water clarity</li>
                  <li><strong>Chlorophyll-a</strong> — algal biomass indicator</li>
                  <li><strong>Temperature</strong> — surface water temperature</li>
                  <li><strong>NDWI</strong> — Normalized Difference Water Index</li>
                </ul>
              </div>
            </div>

            {/* Weather Forecast */}
            <div className="domain-card">
              <div className="domain-header weather">
                <span className="domain-icon">🌤️</span>
                <h3>Weather</h3>
              </div>
              <div className="domain-body">
                <div className="domain-meta">
                  <span className="domain-source">OpenWeather API</span>
                  <span className="domain-availability">Current &amp; 5-day forecast</span>
                </div>
                <p className="domain-desc">Temperature, precipitation, wind and atmospheric conditions for any coordinate.</p>
                <div className="domain-indexes">
                  <span className="index-tag temp">Temperature</span>
                  <span className="index-tag precip">Precipitation</span>
                  <span className="index-tag wind">Wind</span>
                  <span className="index-tag humidity">Humidity</span>
                  <span className="index-tag pressure">Pressure</span>
                  <span className="index-tag visibility">Visibility</span>
                </div>
                <ul className="index-details">
                  <li><strong>Temperature</strong> — current, feels-like, min / max</li>
                  <li><strong>Precipitation</strong> — rain, snow, probability</li>
                  <li><strong>Wind</strong> — speed, gust, direction</li>
                  <li><strong>Humidity / Pressure / Visibility</strong></li>
                </ul>
              </div>
            </div>

            {/* Business / Location */}
            <div className="domain-card">
              <div className="domain-header business">
                <span className="domain-icon">📍</span>
                <h3>Places</h3>
              </div>
              <div className="domain-body">
                <div className="domain-meta">
                  <span className="domain-source">Google Places API</span>
                  <span className="domain-availability">Real-time</span>
                </div>
                <p className="domain-desc">Businesses and points of interest around a location, with ratings and density maps.</p>
                <div className="domain-indexes">
                  <span className="index-tag poi">POI search</span>
                  <span className="index-tag ratings">Ratings</span>
                  <span className="index-tag density">Density</span>
                </div>
                <ul className="index-details">
                  <li><strong>POI search</strong> — restaurants, shops, hospitals, …</li>
                  <li><strong>Ratings</strong> — user ratings and review counts</li>
                  <li><strong>Density</strong> — business density mapping</li>
                </ul>
              </div>
            </div>

            {/* Video / Animation */}
            <div className="domain-card">
              <div className="domain-header video">
                <span className="domain-icon">🎬</span>
                <h3>Time-lapse</h3>
              </div>
              <div className="domain-body">
                <div className="domain-meta">
                  <span className="domain-source">Sentinel-2</span>
                  <span className="domain-availability">June 2015 – present</span>
                </div>
                <p className="domain-desc">Animated GIFs of satellite imagery over time to show environmental change.</p>
                <div className="domain-indexes">
                  <span className="index-tag truecolor">True colour</span>
                  <span className="index-tag ndvi-anim">NDVI</span>
                  <span className="index-tag temporal">Temporal</span>
                </div>
                <ul className="index-details">
                  <li><strong>True colour</strong> — natural RGB composites</li>
                  <li><strong>NDVI</strong> — vegetation change over time</li>
                  <li><strong>Temporal</strong> — monthly / yearly frames</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Built on</h2>
          <p>
            Google Earth Engine for satellite processing, Google Cloud Run and Cloud SQL for the
            service and its records, and Copernicus Sentinel data made available by ESA and the EU.
            Weather and places data come from OpenWeather and Google Maps Platform respectively.
          </p>
        </section>

        <section className="about-section">
          <h2>Limitations worth knowing</h2>
          <ul className="about-list">
            <li>Optical products (Sentinel‑2) depend on cloud‑free scenes; persistently cloudy regions and seasons will have fewer usable images.</li>
            <li>Sentinel‑5P values are column densities at ~5.5 km, suitable for regional trends rather than street‑level readings.</li>
            <li>Results are derived from public datasets as published; they are not a substitute for in‑situ measurements where those are required.</li>
          </ul>
        </section>

        <section className="about-section">
          <h2>Contact</h2>
          <p>
            Questions, dataset requests or collaboration ideas: reach the team through{' '}
            <a href="https://sureal.ai/" target="_blank" rel="noopener noreferrer">sureal.ai</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
